<script lang="ts">import { get } from "svelte/store";
import { onDestroy, onMount } from "svelte";
// External components
import { BannerFixed, BannerSwiper } from "@infinitro/banner-swiper";
import { Card } from "@infinitro/card";
import { favoriteGames, likedGames, recentlyPlayedGames } from "@infinitro/catalog-ex";
import { DEFAULT_PAGE_SIZE, GameCategory, GameSort, getGames, stateCatalogVersion, toGameSlug, toProviderSlug } from "@infinitro/catalog-min";
import { stateRealm, stateUi } from "@infinitro/config";
import { Container } from "@infinitro/container";
import { loggedIn, providerBasePath, toCamelCase, toSiteUrl } from "@infinitro/global";
import { Toolbar } from "@infinitro/tab-category";
import { CasinoTopSection } from "@infinitro/top-section";
let realm;
stateRealm.subscribe((value) => realm = value);
// Props
export let gameCategory = GameCategory.All;
export let games = [];
// State variables
let searchQuery = "";
let providers = [];
let selectedSortBy = GameSort.Popular;
let selectedProvider = "";
let isLoading = false;
let moreGames = true;
let currentPage = 1;
let pageSize = DEFAULT_PAGE_SIZE;
let recently = [];
let favorites = [];
let liked = [];
let sentinel;
// Subscriptions to reactive stores
recentlyPlayedGames.subscribe((value) => recently = value);
favoriteGames.subscribe((value) => favorites = value);
likedGames.subscribe((value) => liked = value);
// Debounce function to limit frequent calls
function debounce(func, delay) {
	let timeout;
	return (...args) => {
		clearTimeout(timeout);
		timeout = setTimeout(() => func(...args), delay);
	};
}
// Function to load more games (pagination)
async function loadMoreGames() {
	if (!moreGames || isLoading) return;
	isLoading = true;
	let [g, _, m] = await getGames(currentPage + 1, pageSize, selectedProvider, gameCategory, selectedSortBy, searchQuery);
	if (g.length) {
		games = [...games, ...g];
		currentPage++;
	}
	moreGames = m;
	isLoading = false;
}
const debouncedLoadMoreGames = debounce(loadMoreGames, 300);
// Function to load initial set of games
const loadGames = async () => {
	console.log("loadGames", gameCategory);
	isLoading = true;
	games = [];
	let [g, p, m] = await getGames(1, pageSize * currentPage, selectedProvider, gameCategory, selectedSortBy, searchQuery);
	games = g;
	providers = p;
	moreGames = m;
	try {
		if (gameCategory === GameCategory.Favorites) {
			if (favorites && favorites.length > 0) {
				games = favorites;
			} else {
				games = favorites || [];
			}
			moreGames = false;
			return;
		} else if (gameCategory === GameCategory.RecentlyPlayed) {
			games = recently;
		} else {
			games = [];
			let [g, p, m] = await getGames(1, pageSize * currentPage, selectedProvider, gameCategory, selectedSortBy, searchQuery);
			games = g;
			providers = p;
			moreGames = m;
		}
	} catch (error) {
		console.log("Error loading games:", error);
	} finally {
		isLoading = false;
	}
};
const debouncedLoadGames = debounce(loadGames, 300);
$: {
	if (gameCategory === GameCategory.Favorites) {
		games = favorites;
	} else if (gameCategory === GameCategory.RecentlyPlayed) {
		games = recently;
	} else {
		debouncedLoadGames();
	}
}
let observer;
let catalogVersionAtMount = 0;
onMount(() => {
	catalogVersionAtMount = get(stateCatalogVersion);
	observer = new IntersectionObserver((entries) => {
		if (entries[0].isIntersecting) {
			debouncedLoadMoreGames();
		}
	}, { rootMargin: "100px" });
	if (sentinel) {
		observer.observe(sentinel);
	} else {
		console.warn("Sentinel element not found");
	}
});
const unsubscribeCatalogVersion = stateCatalogVersion.subscribe((version) => {
	if (version > catalogVersionAtMount) debouncedLoadGames();
});
onDestroy(() => {
	unsubscribeCatalogVersion();
	observer?.disconnect();
});
// Reactive statements
$: {
	if (gameCategory === GameCategory.Favorites) {
		games = favorites;
	} else if (gameCategory === GameCategory.RecentlyPlayed) {
		games = recently;
	} else {
		debouncedLoadGames();
	}
}
$: if (selectedProvider || searchQuery || selectedSortBy) {
	loadGames();
}
$: {
	games;
	providers;
}
$: {
	gameCategory;
	searchQuery;
	selectedSortBy;
	selectedProvider;
	currentPage = 1;
}
$: useDefault = $stateUi.casinoLobby?.casinoLobbyStyle === "default";
$: useAlternative = $stateUi.casinoLobby?.casinoLobbyStyle === "alternative";
$: useAlternative2 = $stateUi.casinoLobby?.casinoLobbyStyle === "alternative2";
$: useAlternative4 = $stateUi.casinoLobby?.casinoLobbyStyle === "alternative4";
</script>

<div class="game-category-container-general">
	<Container>
		<div class="game-category-container">
			{#if useAlternative4}
				<div class="section">
					<BannerFixed />
				</div>
			{:else}
				<div class="section no-mt">
					{#if useDefault || useAlternative2}
						<CasinoTopSection />
					{:else}
						<BannerSwiper showRightSideOfImage={true} />
					{/if}
				</div>
			{/if}
			<div class="section section2">
				<Toolbar
					bind:activeTab={gameCategory}
					bind:searchQuery
					bind:selectedSortBy
					bind:selectedProvider
					{providers}
				/>
			</div>
			<div class="section">
				<div class="games-container">
					{#each games as game, index (`${game.prov ?? ''}-${game.game ?? ''}-${index}`)}
						<a
							id={toCamelCase(game.game)}
							class="game"
							href={toSiteUrl(
								`${providerBasePath(game.prov ?? '')}${toProviderSlug(game.prov ?? '')}/${toGameSlug(game.game)}/`,
								$loggedIn
							)}
						>
							<Card item={game} url="" />
						</a>
					{/each}
					{#if !isLoading && games.length === 0}
						{#if searchQuery}
							<div class="no-games">No games found for matching search query</div>
						{:else}
							<div class="no-games">No games found</div>
						{/if}
					{/if}
					<div id="sentinel" bind:this={sentinel}></div>
				</div>
			</div>
		</div>
		<slot />
	</Container>
</div>

<style>
	.game-category-container-general {
		display: flex;
		width: 100%;
		max-width: 1232px;
		flex-direction: column;
		align-items: center;
}

	.game-category-container {
		display: flex;
		width: 100%;
		flex-direction: column;
}

	.no-games {
		grid-column: 1 / -1;
		width: 100%;
		text-align: center;
		font-size: 1.125rem;
		line-height: 1.75rem;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	.section {
		margin-bottom: 0px;
		margin-top: 0px;
}

	@media (min-width: 1024px) {

		.section {
				margin-bottom: 0.5rem;
				margin-top: 0.5rem;
		}
}

	.section2 {
		margin-bottom: 0px;
}

	.no-mt {
		margin-top: 0px;
}

	.iframe-container {
		position: relative;
		width: 100%;
		padding-bottom: 56.25%;
		height: 0;
	}

	.iframe-container iframe {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		border: none;
	}

	.games-container {
		margin-left: -7px;
		display: grid;
		width: 100%;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		flex-wrap: wrap;
		gap: 8px;
		padding: 7px;
}

	@media (min-width: 640px) {

		.games-container {
				grid-template-columns: repeat(4, minmax(0, 1fr));
		}
}

	@media (min-width: 768px) {

		.games-container {
				grid-template-columns: repeat(6, minmax(0, 1fr));
		}
}

	@media (min-width: 1024px) {

		.games-container {
				grid-template-columns: repeat(8, minmax(0, 1fr));
		}
}

	.games-container {
		width: calc(100% + 14px);
	}

	@media (max-width: 767px) {
		.games-container {
				margin-left: 3px;
				width: 100%;
				padding: 0px;
		}
	}

	.seo-text-container {
		margin-top: 2rem;
		width: 100%;
}

	@media (min-width: 1024px) {
		.games-container {
				gap: 12px;
		}
	}
</style>
